import React, { useState } from 'react';
import {
  LayoutGrid, Settings, ChevronDown,
  Book, GraduationCap, GitFork, HelpCircle, Calendar,
  ClipboardList, FileText, Tag, PartyPopper, Award, ListChecks,
  Users, Mail, Layers, Database, Shield, RefreshCw, Gift, MessageCircle
} from 'lucide-react';

const contentItems = [
  { id: 'Lessons', icon: Book, label: 'Lessons' },
  { id: 'Courses', icon: GraduationCap, label: 'Courses' },
  { id: 'Paths', icon: GitFork, label: 'Paths' },
  { id: 'Questions', icon: HelpCircle, label: 'Questions' },
  { id: 'Surveys', icon: ListChecks, label: 'Surveys' },
  { id: 'Events & Info', icon: Calendar, label: 'Events & Info' },
  { id: 'Submission Forms', icon: ClipboardList, label: 'Submission Forms' },
  { id: 'Submissions', icon: FileText, label: 'Submissions' },
  { id: 'Reinforcements', icon: PartyPopper, label: 'Reinforcements' },
  { id: 'Tags', icon: Tag, label: 'Tags' },
  { id: 'Badges', icon: Award, label: 'Badges' },
];

const settingsItems = [
  { id: 'Manage Users', icon: Users, label: 'Manage Users' },
  { id: 'Invitations', icon: Mail, label: 'Invitations' },
  { id: 'Programs', icon: GraduationCap, label: 'Programs' },
  { id: 'Terms & Conditions', icon: Shield, label: 'Terms & Conditions' },
  { id: 'Groups', icon: Layers, label: 'Groups' },
  { id: 'Referrals', icon: Gift, label: 'Referrals' },
  { id: 'Import/Export Data', icon: Database, label: 'Import/Export' },
  { id: 'Monday Sync', icon: RefreshCw, label: 'Monday Sync' },
  { id: 'Chatbot Q&A', icon: MessageCircle, label: 'Chatbot Q&A' }
];

const contentIds = contentItems.map(i => i.id);

const SettingsSidebar = ({ activeTab, onTabChange, isAdmin }) => {
  const isContentTab = contentIds.includes(activeTab);
  const [contentOpen, setContentOpen] = useState(isContentTab);
  const [settingsOpen, setSettingsOpen] = useState(!isContentTab);

  const handleContentItemClick = (id) => {
    onTabChange(id);
    setContentOpen(true);
    setSettingsOpen(false);
  };

  const handleSettingsItemClick = (id) => {
    onTabChange(id);
    setSettingsOpen(true);
  };

  return (
    <aside className="w-full md:w-72 bg-muted p-4 sm:p-6 rounded-t-lg md:rounded-l-lg md:rounded-tr-none">
      <div className="hidden md:block mb-8">
        <h2 className="text-xl font-bold text-foreground">Admin Panel</h2>
      </div>

      {/* Mobile Navigation — flat grid */}
      <nav className="md:hidden grid grid-cols-3 sm:grid-cols-5 gap-1">
        {[...contentItems, ...settingsItems].map(({ id, icon: Icon, label }) => (
          <button
            key={id}
            className={`flex flex-col items-center justify-center p-2 rounded-lg font-medium transition-colors duration-150 ${
              activeTab === id
                ? 'bg-[#1086a4] text-white'
                : 'text-foreground hover:bg-primary/10 hover:text-primary'
            }`}
            onClick={() => onTabChange(id)}
          >
            <Icon className="w-6 h-6 mb-1 stroke-[1.5]" />
            <span className="text-xs text-center">{label}</span>
          </button>
        ))}
      </nav>

      {/* Desktop Navigation — collapsible sections */}
      <nav className="hidden md:flex md:flex-col gap-1">
        {/* Content Management Section */}
        <button
          onClick={() => setContentOpen(!contentOpen)}
          className="flex items-center w-full px-4 py-3 rounded-lg font-medium text-foreground hover:bg-primary/10 transition-colors duration-150"
        >
          <LayoutGrid className="w-5 h-5 shrink-0 stroke-[1.5]" />
          <span className="ml-3 flex-1 text-left whitespace-nowrap">Content Management</span>
          <ChevronDown className={`w-4 h-4 shrink-0 ml-2 transition-transform duration-200 ${contentOpen ? 'rotate-180' : ''}`} />
        </button>
        {contentOpen && (
          <div className="ml-4 flex flex-col gap-0.5">
            {contentItems.map(({ id, icon: Icon, label }) => (
              <button
                key={id}
                onClick={() => handleContentItemClick(id)}
                className={`flex items-center gap-3 text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150 ${
                  activeTab === id
                    ? 'bg-[#1086a4] text-white shadow-inner'
                    : 'text-foreground hover:bg-primary/10 hover:text-primary'
                }`}
              >
                <Icon className="w-4 h-4 shrink-0 stroke-[1.5]" />
                <span>{label}</span>
              </button>
            ))}
          </div>
        )}

        {/* Settings Section */}
        <button
          onClick={() => setSettingsOpen(!settingsOpen)}
          className="flex items-center w-full px-4 py-3 rounded-lg font-medium text-foreground hover:bg-primary/10 transition-colors duration-150 mt-1"
        >
          <Settings className="w-5 h-5 shrink-0 stroke-[1.5]" />
          <span className="ml-3 flex-1 text-left">Settings</span>
          <ChevronDown className={`w-4 h-4 shrink-0 ml-2 transition-transform duration-200 ${settingsOpen ? 'rotate-180' : ''}`} />
        </button>
        {settingsOpen && (
          <div className="ml-4 flex flex-col gap-0.5">
            {settingsItems.map(({ id, icon: Icon, label }) => (
              <button
                key={id}
                onClick={() => handleSettingsItemClick(id)}
                className={`flex items-center gap-3 text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150 ${
                  activeTab === id
                    ? 'bg-[#1086a4] text-white shadow-inner'
                    : 'text-foreground hover:bg-primary/10 hover:text-primary'
                }`}
              >
                <Icon className="w-4 h-4 shrink-0 stroke-[1.5]" />
                <span>{label}</span>
              </button>
            ))}
          </div>
        )}
      </nav>
    </aside>
  );
};

export default SettingsSidebar;
